import { useAuthStore } from '@/state/store/auth';
import { useUserStore } from '@/store';
import { useCallback, useEffect } from 'react';
import { useOnboardingStepsStore } from '../store/use-onboarding-store';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCompleteOnboarding, useSaveOnboardingProgress } from '../hooks/use-onboarding';
import type { OnboardingWizardProps } from '../types/onboarding.types';
import { StepComplete } from './step-complete';
import { StepInviteTeam } from './step-invite-team';
import { StepOrgSettings } from './step-org-settings';
import { StepProfile } from './step-profile';
import { Stepper } from './stepper';
import { useMyOrganizations } from '@/graphql/queries/organizations';
import { getUserOrgs } from '@/lib/utils/get-user-role';

interface Step {
  id: string;
  title: string;
}

export function OnboardingWizard({
  orgRole,
  orgSlug,
  orgName,
  orgId,
  initialProfile,
  stepsCompleted: initialStepsCompleted,
}: OnboardingWizardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const setOrg = useAuthStore((state) => state.setOrg);
  const isAdmin = orgRole === 'org_admin';
  const { data: dbOrgs } = useMyOrganizations();


  const saveProgressMutation = useSaveOnboardingProgress();
  const completeOnboardingMutation = useCompleteOnboarding();

  const steps: Step[] = [
    { id: 'profile', title: t('ONBOARDING_STEPS_PROFILE_TITLE') },
    ...(orgId
      ? [{ id: 'org_settings', title: t('ONBOARDING_STEPS_ORGSETTINGS_TITLE') }]
      : []),
    ...(isAdmin && orgId
      ? [{ id: 'invite_team', title: t('ONBOARDING_STEPS_INVITETEAM_TITLE') }]
      : []),
    { id: 'complete', title: t('ONBOARDING_STEPS_COMPLETE_TITLE') },
  ];

  const getInitialStep = () => {
    for (let i = 0; i < steps.length - 1; i++) {
      if (!initialStepsCompleted.includes(steps[i].id)) {
        return i;
      }
    }
    return Math.max(steps.length - 1, 0);
  };

  const currentStep = useOnboardingStepsStore((state) => state.currentStep);
  const setCurrentStep = useOnboardingStepsStore((state) => state.setCurrentStep);
  const completedSteps = useOnboardingStepsStore((state) => state.completedSteps);
  const addCompletedStep = useOnboardingStepsStore((state) => state.addCompletedStep);
  const isLoading = useOnboardingStepsStore((state) => state.isLoading);
  const setIsLoading = useOnboardingStepsStore((state) => state.setIsLoading);
  const profileData = useOnboardingStepsStore((state) => state.profileData);
  const setProfileData = useOnboardingStepsStore((state) => state.setProfileData);
  const orgData = useOnboardingStepsStore((state) => state.orgData);
  const setOrgData = useOnboardingStepsStore((state) => state.setOrgData);

  // Initialize store on mount
  useEffect(() => {
    useOnboardingStepsStore.setState({
      currentStep: getInitialStep(),
      completedSteps: new Set(initialStepsCompleted),
      profileData: {
        firstName: initialProfile.firstName ?? '',
        lastName: initialProfile.lastName ?? '',
        jobTitle: initialProfile.jobTitle ?? '',
        department: initialProfile.department ?? '',
      },
      orgData: { name: orgName ?? '' },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStepComplete = useCallback(
    async (stepId: string) => {
      if (!user?.itemId) return;
      setIsLoading(true);
      try {
        await saveProgressMutation.mutateAsync({
          userId: user.itemId,
          stepId,
        });
        addCompletedStep(stepId);
        setCurrentStep(Math.min(currentStep + 1, steps.length - 1));
      } finally {
        setIsLoading(false);
      }
    },
    [user, setIsLoading, saveProgressMutation, addCompletedStep, setCurrentStep, currentStep, steps.length]
  );

  const handleComplete = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      await completeOnboardingMutation.mutateAsync(user.itemId);

      // Redirect based on org/role context — mirrors OnboardingPage's
      // already-onboarded redirect logic so both entry points behave the same.
      const realOrgs = getUserOrgs(user);
      const userOrgs = realOrgs.map((m) => {
        const orgInDb = dbOrgs?.blocksOrgs?.find((o) => o.BlocksOrgId === m.organizationId);

        // Skip orgs that aren't found in DB or lack a slug (shouldn't happen).
        if (!orgInDb || !orgInDb.Slug) {
          return null;
        }

        return {
          organizationId: m.organizationId,
          roles: m.roles,
          slug: orgInDb.Slug,
          UDSItemId: orgInDb.ItemId,
          sigOrgId: orgInDb.SigOrgId,
        };
      });

      const platformRole = useAuthStore.getState().platformRole;

      // Admin (or editor) with no real orgs — go to admin console.
      if (
        userOrgs.filter(Boolean).length === 0 &&
        (platformRole === 'sys_admin' || platformRole === 'sys_editor')
      ) {
        navigate('/admin', { replace: true });
        return;
      }

      // Already have an active org context (e.g. came in via an org invite).
      if (orgSlug) {
        navigate(`/org/${orgSlug}`, { replace: true });
        return;
      }

      // Single user org — auto-select and redirect.
      if (userOrgs.filter(Boolean).length === 1) {
        const matchedOrg = userOrgs.find(Boolean);
        if (matchedOrg) {
          setOrg(matchedOrg.organizationId, matchedOrg.slug, matchedOrg.UDSItemId, matchedOrg.sigOrgId);
          navigate(`/org/${matchedOrg.organizationId}`, { replace: true });
          return;
        }
      }

      // Multiple user orgs or unlinked signature orgs — show org selector.
      if (userOrgs.filter(Boolean).length > 1 || dbOrgs?.sigOrgsWithoutBlocks?.length) {
        navigate('/org/select', { replace: true });
        return;
      }

      // No orgs — go to create.
      navigate('/org/new', { replace: true });
    } catch (error) {
      console.error('Failed to complete onboarding:', error);
      setIsLoading(false);
    }
  }, [user, setIsLoading, completeOnboardingMutation, orgSlug, dbOrgs?.sigOrgsWithoutBlocks?.length, dbOrgs?.blocksOrgs, navigate, setOrg]);

  const handleSkip = useCallback(() => {
    setCurrentStep(Math.min(currentStep + 1, steps.length - 1));
  }, [currentStep, steps.length, setCurrentStep]);

  const renderStep = () => {
    const step = steps[currentStep];

    switch (step.id) {
      case 'profile':
        return (
          <StepProfile
            data={profileData}
            onChange={setProfileData}
            onComplete={() => handleStepComplete('profile')}
            isLoading={isLoading}
          />
        );
      case 'org_settings':
        return (
          <StepOrgSettings
            orgId={orgId}
            data={orgData}
            onChange={setOrgData}
            onComplete={() => handleStepComplete('org_settings')}
            onSkip={handleSkip}
            isLoading={isLoading}
          />
        );
      case 'invite_team':
        return (
          <StepInviteTeam
            orgId={orgId}
            onComplete={() => handleStepComplete('invite_team')}
            onSkip={handleSkip}
            isLoading={isLoading}
          />
        );
      case 'complete':
        return (
          <StepComplete
            orgName={orgData.name || orgName}
            orgSlug={orgSlug}
            onComplete={handleComplete}
            isLoading={isLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-foreground">
          {orgName
            ? t('ONBOARDING_TITLE', { orgName: orgData.name || orgName })
            : t('ONBOARDING_TITLENOORG')}
        </h1>
        <p className="mt-2 text-muted-foreground">{t('ONBOARDING_SUBTITLE')}</p>
      </div>

      <Stepper steps={steps} currentStep={currentStep} orientation="horizontal" />

      <div className="hidden">{completedSteps}</div>

      <div className="rounded-lg border bg-background p-6 shadow-sm">{renderStep()}</div>
    </div>
  );
}
